import { useEffect, useRef } from "react";
import create from "zustand";
import { Rules, TableRow } from "../interfaces/TableRow";

const PESO_TIPO_REFERENCIA = {
  plantio: 1,
  emergencia: 2,
  colheita: 3,
  outro: 0,
} as const;

function normalizarTextoReferencia(valor?: string) {
  return String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

export function getPesoTipoReferencia(rules?: Rules): number {
  if (!rules) return PESO_TIPO_REFERENCIA.outro;

  const codigo = String(rules.codigo ?? "").trim().toUpperCase();
  const coluna = normalizarTextoReferencia(rules.colunaTalhaoDataReferencia);
  const descricao = normalizarTextoReferencia(rules.descricao);

  if (codigo === "C") return PESO_TIPO_REFERENCIA.outro;

  const ehPlantio =
    codigo === "P" ||
    coluna.includes("plantio") ||
    descricao === "plantio" ||
    descricao.includes("data de plantio") ||
    descricao.includes("data plantio");

  const ehEmergencia =
    codigo === "E" ||
    coluna.includes("emergenc") ||
    descricao === "emergencia" ||
    descricao.includes("data de emergenc") ||
    descricao.includes("data emergenc");

  const ehColheita =
    codigo === "CO" ||
    codigo === "COL" ||
    codigo === "L" ||
    coluna.includes("colheita") ||
    descricao === "colheita" ||
    descricao.includes("data de colheita") ||
    descricao.includes("data colheita");

  if (ehPlantio) return PESO_TIPO_REFERENCIA.plantio;
  if (ehEmergencia) return PESO_TIPO_REFERENCIA.emergencia;
  if (ehColheita) return PESO_TIPO_REFERENCIA.colheita;

  return PESO_TIPO_REFERENCIA.outro;
}

function chaveGrupoOperacao(row: TableRow): string {
  return [
    row.operacao?.value ?? "",
    row.referencia ?? "",
    row.carencia ?? "",
    row.vinculo ?? "",
    row.dtInicio ?? "",
    row.dtFim ?? "",
  ].join("||");
}

function proximaSequenciaLivre(usadas: Set<number>) {
  let sequencia = 1;
  while (usadas.has(sequencia)) sequencia += 1;
  usadas.add(sequencia);
  return sequencia;
}

export function garantirSequenciaAntesDeOrdenar(rows: TableRow[]): TableRow[] {
  const usadas = new Set(
    rows
      .map((row) => Number(row.sequencia))
      .filter((sequencia) => sequencia > 0)
  );
  const sequenciaPorChave = new Map<string, number>();

  return rows.map((row) => {
    if (Number(row.sequencia) > 0) return row;

    const chave = chaveGrupoOperacao(row);
    if (!sequenciaPorChave.has(chave)) {
      sequenciaPorChave.set(chave, proximaSequenciaLivre(usadas));
    }

    return { ...row, sequencia: sequenciaPorChave.get(chave)! };
  });
}

interface TableState {
  indexId: number;
  rows: TableRow[];
  rowsCopy: TableRow[];
  tempIdDeleteds: string[];

  addRow: (row: TableRow) => void;
  updateRow: (index: number, updated: Partial<TableRow>) => void;
  removeRow: (index: number) => void;
  clearRows: () => void;

  setIndex: (index: number) => void;
  setRows: (rows: TableRow[]) => void,
  setRowsCopy: (rows: TableRow[]) => void;
  getChanges: () => TableRow[];
  getChangesDiff: () => TableRow[];
  setTempIdDeleteds: (ids: string[]) => void;
  updateTableRow: (rows: TableRow[]) => void;
}

function getNextSequence(rows: TableRow[]) {
  const used = new Set(rows.map((r) => r.sequencia));
  let i = 1;
  while (used.has(i)) i += 1;
  return i;
}

function isFilledValue(v: any) {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim() !== "";
  return String(v).trim() !== "";
}

export function findMatchingRow(row: TableRow, candidates: TableRow[]): TableRow | undefined {
  if (row.planoOperacionalOperacaoComponenteId) {
    return candidates.find(
      (o) => o.planoOperacionalOperacaoComponenteId === row.planoOperacionalOperacaoComponenteId
    );
  }
  if (row.tempId !== undefined && row.tempId !== null) {
    return candidates.find((o) => o.tempId === row.tempId);
  }
  return undefined;
}

function countBySequencia(rowsList: TableRow[]): Map<number, number> {
  const map = new Map<number, number>();
  rowsList.forEach((r) => {
    map.set(r.sequencia, (map.get(r.sequencia) ?? 0) + 1);
  });
  return map;
}

export const useTableForm = create<TableState>((set, get) => ({
  indexId: -1,
  rows: [],
  rowsCopy: [],
  tempIdDeleteds: [],

  setIndex(indexId) {
    set({ indexId });
  },

  setTempIdDeleteds(ids) {
    set({ tempIdDeleteds: ids });
  },

addRow: (newRow) =>
  set((state) => {
    const isEmpty =
      !isFilledValue(newRow.operacao.value) &&
      !isFilledValue(newRow.referencia) &&
      !isFilledValue(newRow.carencia) &&
      !isFilledValue(newRow.vinculo) &&
      !isFilledValue(newRow.dtInicio) &&
      !isFilledValue(newRow.dtFim);

    let sequencia = getNextSequence(state.rows);

    if (!isEmpty) {
      const duplicate = state.rows.find((r) => {
        const sameOperacao =
          isFilledValue(r.operacao.value) &&
          r.operacao.value === newRow.operacao.value;

        const sameReferencia =
          isFilledValue(r.referencia) &&
          r.referencia === newRow.referencia;

        const sameCarencia =
          isFilledValue(r.carencia) &&
          isFilledValue(newRow.carencia) &&
          r.carencia === newRow.carencia;

        const sameVinculo =
          isFilledValue(r.vinculo) &&
          isFilledValue(newRow.vinculo) &&
          r.vinculo === newRow.vinculo;

        const samePeriodo =
          isFilledValue(r.dtInicio) &&
          isFilledValue(r.dtFim) &&
          isFilledValue(newRow.dtInicio) &&
          isFilledValue(newRow.dtFim) &&
          r.dtInicio === newRow.dtInicio &&
          r.dtFim === newRow.dtFim;

        let res = null;

        if (r.vinculo || newRow.vinculo) {
          res = sameOperacao && sameReferencia && sameVinculo && (sameCarencia || samePeriodo);
        } else {
          res= sameOperacao && sameReferencia && (sameCarencia || samePeriodo);
        }
        
          return res;
        });

      if (duplicate) sequencia = duplicate.sequencia;
    }

    const newRows = [...state.rows, { ...newRow, sequencia }].sort(
      (a, b) => (a.sequencia ?? 0) - (b.sequencia ?? 0)
    );

    return { rows: newRows };
  }),

updateRow: (index, updated) =>
  set((state) => {
    if (index < 0 || index >= state.rows.length) return { rows: state.rows };

    const updatedRows = [...state.rows];
    const current = updatedRows[index];
    let merged = { ...current, ...updated };

    const isEmpty =
      !isFilledValue(merged.operacao.value) &&
      !isFilledValue(merged.referencia) &&
      !isFilledValue(merged.carencia) &&
      !isFilledValue(merged.vinculo) &&
      !isFilledValue(merged.dtInicio) &&
      !isFilledValue(merged.dtFim);

    if (isEmpty) {
      updatedRows[index] = merged;
      return { rows: updatedRows };
    }

    const duplicate = state.rows.find((x, i) => {
      if (i === index) return false;

      const sameOperacao =
        isFilledValue(x.operacao.value) &&
        x.operacao.value === merged.operacao.value;

      const sameReferencia =
        isFilledValue(x.referencia) && x.referencia === merged.referencia;

      const sameCarencia =
        isFilledValue(x.carencia) &&
        isFilledValue(merged.carencia) &&
        x.carencia === merged.carencia;

      const sameVinculo =
        isFilledValue(x.vinculo) &&
        isFilledValue(merged.vinculo) &&
        x.vinculo === merged.vinculo;

      const samePeriodo =
        isFilledValue(x.dtInicio) &&
        isFilledValue(x.dtFim) &&
        isFilledValue(merged.dtInicio) &&
        isFilledValue(merged.dtFim) &&
        x.dtInicio === merged.dtInicio &&
        x.dtFim === merged.dtFim;

      let res = null;
      if (x.vinculo || merged.vinculo) {
        res = sameOperacao && sameReferencia && sameVinculo && (sameCarencia || samePeriodo);
      } else {
        res= sameOperacao && sameReferencia && (sameCarencia || samePeriodo);
      }
      
      return res;
    });

    if (duplicate) {
        const minSeq = Math.min(
          duplicate.sequencia ?? Infinity,
          current.sequencia ?? Infinity
        );

        merged.sequencia = minSeq;
        duplicate.sequencia = minSeq;
    } else {
        const otherRows = state.rows.filter((_, i) => i !== index);
        merged.sequencia = getNextSequence(otherRows);

    }

    updatedRows[index] = merged;

    let newRows = updatedRows;

    return { rows: newRows };
  }),


updateTableRow: (newRow: TableRow[]) => {
  set((state) => ({
    rows: newRow,
  }));
},


removeRow: (index) =>
  set((state) => {
    const newRows = state.rows.filter((_, i) => i !== index);
    return { rows: newRows };
  }),

  clearRows: () => set({ rows: [], rowsCopy: [] }),

  setRows: (rows: TableRow[]) => set({ rows }),
  setRowsCopy: (rows: TableRow[]) => set({ rowsCopy: JSON.parse(JSON.stringify(rows)) }),

  getChanges: () => {
    const { rows, rowsCopy } = get();

    const contagemAtual = countBySequencia(rows);
    const contagemOriginal = countBySequencia(rowsCopy);

    const sequenciasComRemocaoParcial = new Set<number>();
    contagemOriginal.forEach((qtdOriginal, sequencia) => {
      const qtdAtual = contagemAtual.get(sequencia) ?? 0;
      if (qtdAtual > 0 && qtdAtual < qtdOriginal) {
        sequenciasComRemocaoParcial.add(sequencia);
      }
    });

    return rows.filter((row) => {
      if (sequenciasComRemocaoParcial.has(row.sequencia)) return true;

      const original = findMatchingRow(row, rowsCopy);
      if (!original) return true;

      return JSON.stringify(row) !== JSON.stringify(original);
    });
  },


  getChangesDiff: () => {
    const { rows, rowsCopy } = get();

    return rowsCopy.filter((row) => {
      const atual = findMatchingRow(row, rows);
      if (!atual) return true;

      return JSON.stringify(row) !== JSON.stringify(atual);
    });
  },

}));

export function ordenarOperacoesPorCarencia(listaOriginal: TableRow[]): TableRow[] {
  const extrairSequenciaVinculada = (vinculo?: string): number | null => {
    if (!vinculo) return null;
    const match = vinculo.match(/\d+/);
    return match ? Number(match[0]) : null;
  };

  const isVinculada = (item: TableRow) =>
    item.rules?.informarOperacaoVinculada === true;

  const lista = listaOriginal.map((item, index) => ({
    ...item,
    __indexOriginal: index,
    __seqOriginal: item.sequencia ?? 0,
  }));

  const gruposMap = new Map<number, typeof lista>();

  for (const item of lista) {
    const seq = item.__seqOriginal;
    if (!gruposMap.has(seq)) gruposMap.set(seq, []);
    gruposMap.get(seq)!.push(item);
  }

  const grupos = Array.from(gruposMap.entries()).map(([seq, itens]) => {
    const base = itens[0];

    return {
      seqOriginal: seq,
      itens,
      carencia: Number(base.carencia ?? 0),
      pesoTipo: getPesoTipoReferencia(base.rules),
      seqPai: isVinculada(base)
        ? extrairSequenciaVinculada(base.vinculo)
        : null,
      ordemOriginal: Math.min(...itens.map(i => i.__indexOriginal)),
    };
  });

  const grupoPorSeq = new Map<number, typeof grupos[number]>();
  for (const g of grupos) grupoPorSeq.set(g.seqOriginal, g);

  const calcularPeso = (g: typeof grupos[number], visitados = new Set<number>()): number => {
    if (visitados.has(g.seqOriginal)) return g.carencia;
    visitados.add(g.seqOriginal);

    if (!g.seqPai) return g.carencia;

    const pai = grupoPorSeq.get(g.seqPai);
    if (!pai) return g.carencia;

    return calcularPeso(pai, visitados) + g.carencia;
  };

  grupos.sort((a, b) => {
    const tipoA = a.pesoTipo;
    const tipoB = b.pesoTipo;

    if (tipoA !== tipoB) {
      if (tipoA !== PESO_TIPO_REFERENCIA.outro && tipoB !== PESO_TIPO_REFERENCIA.outro) {
        return tipoA - tipoB;
      }

      if (tipoA === PESO_TIPO_REFERENCIA.plantio) return -1;
      if (tipoB === PESO_TIPO_REFERENCIA.plantio) return 1;
      if (tipoA === PESO_TIPO_REFERENCIA.colheita) return 1;
      if (tipoB === PESO_TIPO_REFERENCIA.colheita) return -1;
    }

    const pesoA = calcularPeso(a);
    const pesoB = calcularPeso(b);

    if (pesoA !== pesoB) return pesoA - pesoB;

    if (a.seqPai == null && b.seqPai != null) return -1;
    if (a.seqPai != null && b.seqPai == null) return 1;

    return a.ordemOriginal - b.ordemOriginal;
  });

  const resultadoGrupos: typeof grupos = [];
  const visitados = new Set<number>();

  const montar = (g: typeof grupos[number]) => {
    if (visitados.has(g.seqOriginal)) return;
    visitados.add(g.seqOriginal);

    if (g.seqPai != null) {
      const pai = grupoPorSeq.get(g.seqPai);
      if (pai) montar(pai);
    }

    resultadoGrupos.push(g);

    for (const filho of grupos) {
      if (filho.seqPai === g.seqOriginal) {
        montar(filho);
      }
    }
  };

  for (const g of grupos) {
    montar(g);
  }

  const resultado: typeof lista = [];
  const mapaNovaSeq = new Map<number, number>();

  let seq = 1;

  for (const grupo of resultadoGrupos) {
    mapaNovaSeq.set(grupo.seqOriginal, seq);

    for (const item of grupo.itens) {
      resultado.push({
        ...item,
        sequencia: seq,
      });
    }

    seq++;
  }

  for (const item of resultado) {
    if (!isVinculada(item)) continue;

    const seqPaiOriginal = extrairSequenciaVinculada(item.vinculo);
    if (seqPaiOriginal == null) continue;

    const novaSeqPai = mapaNovaSeq.get(seqPaiOriginal);
    if (novaSeqPai == null) continue;

    if (novaSeqPai === item.sequencia) continue;

    item.vinculo = `Operação ${novaSeqPai}`;
  }

  return resultado.map(({ __indexOriginal, __seqOriginal, ...item }) => item);
}

export function recalcularSequenciaOperacoes(rows: TableRow[]): TableRow[] {
  return ordenarOperacoesPorCarencia(garantirSequenciaAntesDeOrdenar(rows));
}

export function useReferenciaChangeWatcher(
  callback: (changedIndex: number, newValue: string) => void
) {
  const rows = useTableForm((s) => s.rows);
  const prevRef = useRef<typeof rows>([]);

  useEffect(() => {
    const prevRows = prevRef.current;

    if (prevRows.length > 0 && rows.length > 0) {
      rows.forEach((row, index) => {
        const prevRow = prevRows[index];

        if (!prevRow) return;

        if (row.referencia !== prevRow.referencia) {
          callback(index, row.referencia);
        }
      });
    }

    prevRef.current = rows;
  }, [rows, callback]);
}


export function useChangeWatcher(
  callback: (duplicatedIndex: number, componenteValue: string) => void
) {
  const rows = useTableForm((s) => s.rows);

  const triggeredKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const mapFirstIndex = new Map<string, number>();
    const currentKeys: string[] = [];

    const makeKey = (r: TableRow) =>
      `${r.referencia ?? ""}||${r.operacao.value ?? ""}||${r.carencia ?? ""}||${r.componente ?? ""}||${r.vinculo ?? ""}`;

    rows.forEach((r, i) => {
      const vinculoFilled = isFilledValue(r.vinculo) ? isFilledValue(r.vinculo) : null;
      let allFilled =
        isFilledValue(r.referencia) &&
        isFilledValue(r.operacao.value) &&
        isFilledValue(r.carencia) &&
        isFilledValue(r.componente);

      currentKeys[i] = makeKey(r);
      if (vinculoFilled !== null) {
        allFilled = allFilled && vinculoFilled;
      }
      if (allFilled) {
        if (!mapFirstIndex.has(currentKeys[i])) {
          mapFirstIndex.set(currentKeys[i], i);
        }
      }
    });

    const newTriggered = new Set<string>();

    rows.forEach((r, i) => {
      const key = currentKeys[i];
      if (!key) return;
      const vinculoFilled = isFilledValue(r.vinculo) ? isFilledValue(r.vinculo) : null;
      let allFilled =
        isFilledValue(r.referencia) &&
        isFilledValue(r.operacao.value) &&
        isFilledValue(r.carencia) &&
        isFilledValue(r.componente);

      if (vinculoFilled !== null) {
        allFilled = allFilled && vinculoFilled;
      }

      if (!allFilled) {
        return;
      }

      const firstIdx = mapFirstIndex.get(key);
      if (firstIdx === undefined) return;

      if (firstIdx < i) {
        if (!triggeredKeysRef.current.has(key)) {
          callback(i, r.componente);
          newTriggered.add(key);
        } else {
          newTriggered.add(key);
        }
      }
    });

    triggeredKeysRef.current = newTriggered;

  }, [rows, callback]);
}